import React, { useEffect } from 'react';
import type { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch } from '../../store/store';
import AlertLoader from 'syf-component-library/ui/atoms/Loader/Skeleton/AlertLoader';
import BackToCardSummaryLink from '../accountList/linkButtons/BackToCardSummaryLink';
import ShoppingPassError from './ShoppingPassError';
import ShoppingPassInfo from './ShoppingPassInfo';
import { getSessionId } from '../../store/sessionSlice';
import { getShoppingPassContent } from '../../store/contentSlice';
import {
  fetchPaymentToken,
  getSelectedTokenAccount,
} from '../../store/paymentTokenSlice';
import {
  PAYMENT_TOKEN_TYPES,
  CLIENT_ID_PAYSOL,
} from '../../utils/constants/genericConstants';
import { detectDeviceType } from '../../utils/helpers';
import {
  captureClickAnalyticsObject,
  captureAnalyticsDataAttributes,
} from '../../utils/analytics/analyticsDispatchers';
import './shoppingPass.css';

const ShoppingPass: FunctionComponent = (props: any) => {
  const { account, isAccordionCollapsed } = props;
  const dispatch = useAppDispatch();
  const sessionId = useSelector(getSessionId);
  const selectedAccount = useSelector(getSelectedTokenAccount);
  const shoppingPassContent = useSelector(getShoppingPassContent);

  const {
    encodedBarcode,
    tokenNumber,
    tokenCvv,
    tokenExpiryDate,
    isGenerateCardLoading,
    errorWhileFetchingToken,
    last4Accountnumber,
  } = selectedAccount;

  const {
    headingText,
    generatePassButtonText,
    accountNumberText,
    expirationText,
    cvvText,
    presentAtCheckoutText,
    backToCardSummaryText,
    inStoreCheckoutText,
    errorText,
  }: any = shoppingPassContent;

  const selectedClientId =
    account.clientId === CLIENT_ID_PAYSOL
      ? account.portfolio
      : account.clientId;

  const shoppingPassProps = {
    encodedBarcode,
    last4Accountnumber,
    tokenNumber,
    tokenExpiryDate,
    tokenCvv,
    accountNumberText,
    expirationText,
    cvvText,
    presentAtCheckoutText,
    inStoreCheckoutText,
    errorText,
    selectedClientId,
  };

  /**
   * single use card is in expanded state if any of below 3 are true
   * 1. tokenNumber is present, meaning the token was generated already in the session
   * 2. OR isGenerateCardLoading: API is pending
   * 3. OR errorWhileFetchingToken: API has errored out
   * */
  const expandCardState =
    encodedBarcode || isGenerateCardLoading || errorWhileFetchingToken;

  const shoppingPassBarcodeState =
    (tokenNumber || encodedBarcode) && !isGenerateCardLoading;

  /** Css class modifications based on js start **/
  const generateSingleCardContentClass =
    errorWhileFetchingToken && !isGenerateCardLoading
      ? 'generate-shopping-pass-content generate-card-content-error'
      : 'generate-shopping-pass-content';

  let isClickAllowedClass = 'click-allowed';
  let spinArrowIconClass = 'arrow-rotate';
  let wrapperClass = '';
  let isGenerateCardOpenClass = '';

  if (isGenerateCardLoading) {
    isClickAllowedClass = 'click-not-allowed';
    spinArrowIconClass = 'animate-spin arrow-rotate';
  }

  if (expandCardState) {
    wrapperClass = 'shopping-pass-card-wrapper-expanded';
    isGenerateCardOpenClass =
      'generate-button generate-shopping-pass-button-open';
  } else {
    wrapperClass = 'shopping-pass-card-wrapper';
    isGenerateCardOpenClass = 'generate-button generate-shopping-pass-button';
  }

  const generateShoppingPassButtonOnly =
    !expandCardState || (expandCardState && errorWhileFetchingToken);
  const showInfoTextWithButton =
    generateShoppingPassButtonOnly && !errorWhileFetchingToken;
  const generateCardButtonClass = `${isGenerateCardOpenClass} ${isClickAllowedClass}`;
  /** Css class modifications based on js end **/

  const generateNewShoppingPass = (event: any) => {
    if (!isGenerateCardLoading) {
      dispatch(
        fetchPaymentToken({
          sessionId,
          accountId: account.accountId,
          tokenType: PAYMENT_TOKEN_TYPES.SHOPPING_BARCODE,
          selectedClient: selectedClientId,
        }),
      );
      captureAnalyticsDataAttributes(event);
    }
  };

  useEffect(() => {
    if (errorWhileFetchingToken) {
      captureClickAnalyticsObject({
        dataReason: `failedGenSingleUseCard:${account.displayName}`,
        dataChannel: 'failedCreatingNewPass',
        dataObject: 'button',
        dataType: detectDeviceType,
      });
    }
  }, [errorWhileFetchingToken]);

  return (
    <>
      <div className={wrapperClass}>
        <div className="shopping-pass-card-header-wrapper">
          <span className="shopping-pass-card-header">{headingText}</span>
        </div>
        <div className="shopping-pass-card-content">
          {generateShoppingPassButtonOnly && (
            <div
              className={generateCardButtonClass}
              data-type={detectDeviceType}
              data-reason={`genSingleUseCard:${account.displayName}`}
              data-object="button"
              data-channel="successPassDetails"
              onClick={generateNewShoppingPass}
              data-testid="generate-new-shopping-pass-card"
            >
              <div className="arrow-rotate-wrapper">
                <FontAwesomeIcon
                  icon={faArrowsRotate}
                  className={spinArrowIconClass}
                />
              </div>
              <div className="generate-shopping-pass-button-text-wrapper">
                <span className="generate-shopping-pass-button-text">
                  {generatePassButtonText}
                </span>
              </div>
            </div>
          )}
          {showInfoTextWithButton && (
            <div className="card-disclosure-wrapper">
              <div className="card-disclosure-heading">
                {inStoreCheckoutText}
              </div>
            </div>
          )}
          {expandCardState && (
            <div className={generateSingleCardContentClass}>
              {isGenerateCardLoading && (
                <div className="alert-skeleton-content">
                  <AlertLoader />
                </div>
              )}
              {errorWhileFetchingToken && !isGenerateCardLoading && (
                <div
                  className="shopping-pass-block"
                  data-testid="generate-single-card-error"
                >
                  <ShoppingPassError {...shoppingPassProps} />
                </div>
              )}
              {shoppingPassBarcodeState && (
                <ShoppingPassInfo {...shoppingPassProps} />
              )}
            </div>
          )}
        </div>
      </div>
      <BackToCardSummaryLink
        accountId={account.accountId}
        displayName={account.displayName}
        backToCardSummaryText={backToCardSummaryText}
        isAccordionCollapsed={isAccordionCollapsed}
      />
    </>
  );
};

export default ShoppingPass;
